import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import QuoteBuilderManagePanel from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderManagePanel.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import QuoteBuilderNavSection from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderNavSection.jsx';
import PresentedDielineOptions from 'components/layout_and_quote_builder/quote_builder/PresentedDielineOptions.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import frankStyles from 'components/frank/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import { CacheProvider } from 'components/layout_and_quote_builder/quote_builder/CacheContext.jsx';
import ErrorBoundary from 'components/common/ErrorBoundary.jsx';
import { readSelectedManufacturingLineList, writeSelectedManufacturingLineList, clearSelectedManufacturingLineList }
  from 'components/frank/utils/manufacturingLineListUrlState.js';
import { clearSelectedSolution } from 'components/layout_and_quote_builder/utils/solutionUrlState.js';
import { linkedParentOf } from 'components/frank/utils/linkedNavSection.js';
import { DebugPopupProvider } from 'components/layout_and_quote_builder/DebugPopup.jsx';
import FrankSetupWizard from 'components/frank/FrankSetupWizard.jsx';
import StandardManufacturingLinesToggle from 'components/frank/StandardManufacturingLinesToggle.jsx';
import PostRequest from 'components/common/PostRequest.jsx';
import { manufacturingLinesSource as readManufacturingLinesSource, setManufacturingLinesSource, STANDARD }
  from 'components/frank/utils/manufacturingLinesSource.js';
import { hydrateResponseCache, clearResponseCache } from 'components/frank/utils/ManufacturingLineResponseCache.js';

// How long the page will wait for the persisted response cache before rendering without it. Reading a quote's
// stored answers out of IndexedDB is a few milliseconds, so this is not a budget -- it is the guarantee that a
// wedged or blocked store (another tab holding an upgrade, a browser that stalls in private mode) costs the
// user a re-fetch and not a blank page.
const CACHE_HYDRATION_TIMEOUT_MILLISECONDS = 3000;

// Frank's tabbed front end for the Manufacturing Cost Calculator, served at /mcc. It reuses the same content
// components the legacy stacked page (ShowLayoutAndQuoteBuilderPage) renders -- they already save to the
// user_quote, handle read-only, currency and status -- but lays them out as tabs instead of one long page:
//   Manage (quantity / windows / currency + status) | one tab per nav-builder section (e.g. Carton) |
//   Customer (name / email / phone / notes) | Dieline (material + dimensions).
// The section tabs render LayoutNavSectionList with a single section, so the existing Frank-mode gating
// (fetch Frank's section-material-sheets endpoint, hide the manufacturing lines + solutions) carries over.
const FrankQuoteBuilderPage = ({
  config, navSections, dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale,
  theme, btn_action, dimension_texts_on_layouts_enabled, user_quote_id, dieline_svg, header
}) => {
  const LayoutContextValues = {
    dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale, theme, btn_action,
    dimension_texts_on_layouts_enabled,
  };

  // The tab bar is TWO levels. The TOP level is one tab per nav-builder part section (e.g. Carton) plus the
  // global Manage, Customer, Dieline and General -- a single row that does not wrap. The 'general' section is
  // the nav builder's catch-all, not a manufacturing part_type, so it gets no PART tab (it stays out of
  // priceableSections and so gets no Manufacturing Line -> Solution -> Pricing stages); instead it gets a
  // global tab alongside Manage / Customer / Dieline that renders its quote-wide pricing sub-sections (Profit
  // Margin, Transport, ...). The SECOND level, shown only for the active PART section, is that section's three
  // stages (Manufacturing Line -> Solution -> Pricing): the three views of its ManufacturingLineLists (the
  // listing, the solution cards, the priced view). The user picks a manufacturing line, then a solution, then
  // sees the price -- a sequence, so the stages are numbered.
  const priceableSections = navSections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general');

  // The nav builder's 'general' catch-all section -- its members are the quote-wide pricing sub-sections
  // (Profit Margin, Transport, ...), priced against the whole quote rather than one part. It carries its own
  // drawing as the item those sub-sections hang off (ManufacturingCostCalculatorNavBuilder#drawing_data).
  const generalSection = navSections.find((section) => section.part_type === 'general');

  // The three stage tabs a section is split into, in order. `stage` is the ManufacturingLineLists stageView.
  const sectionStageTabs = [
    { suffix: 'line', stage: 'line', labelKey: 'quote_builder.frank_tab_manufacturing_line' },
    { suffix: 'solution', stage: 'solution', labelKey: 'quote_builder.frank_tab_solution' },
    { suffix: 'pricing', stage: 'pricing', labelKey: 'quote_builder.frank_tab_pricing' },
  ];
  const sectionTabs = priceableSections.flatMap(({ section, index }) => sectionStageTabs.map((stageTab) => ({
    key: `section_${index}_${stageTab.suffix}`,
    label: `${section.label || section.part_type} - ${t(stageTab.labelKey)}`,
    sectionIndex: index,
    stage: stageTab.stage,
  })));

  // The top-level tabs: ONE per priceable section (labelled by the section only -- the stage no longer rides
  // in the label, so "Carton for lamination" appears once, not three times), then the Profit & Additional
  // Costs tab (itself a section), then the global Customer and Dieline. This keeps the bar to a single row.
  const topTabs = [
    { key: 'manage', label: t('quote_builder.manage') },
    ...priceableSections.map(({ section, index }) => ({
      key: `section_${index}`,
      label: section.label || section.part_type,
      sectionIndex: index,
    })),
    // Its OWN tab label ("Profit & Additional Costs" / "Kar & Ek Maliyetler") -- names what the tab holds
    // (profit margin plus the additional cost lines), rather than reusing the section's generic "General" /
    // "Genel" label, which stays as-is for the legacy page. Only offered when the nav builder produced a
    // general section. Sits right after the part sections -- it is itself a section, so the section tabs stay
    // in order -- and before the global Customer and Dieline tabs.
    ...(generalSection ? [{ key: 'general', label: t('quote_builder.frank_tab_general') }] : []),
    { key: 'customer', label: t('quote_builder.customer') },
    { key: 'dieline', label: t('quote_builder.dieline') },
  ];

  // Seed from the quote's own quantity (falling back to 1000, the create default), NOT a hardcoded 1000: the
  // server scores/solves against user_quote.count, and count is part of the ManufacturingLineResponseCache key,
  // so the page's count MUST match the quote's on load -- otherwise a reopened quote whose quantity is not 1000
  // would file its results under the wrong (count=1000) key. Mirrors FrankSetupWizard's seed.
  const [count, setCount] = useState((window.user_quote && window.user_quote.count) || 1000);

  // The quote-wide manufacturing-lines source (the "Standard Manufacturing Lines" example account vs the owner's
  // own account). Seeded from the window global that frank/edit.html.erb rendered from the persisted column.
  const [manufacturingLinesSource, setSourceState] = useState(readManufacturingLinesSource());

  // CLEAR EVERY SECTION'S SELECTION BEFORE A SOURCE SWITCH, keeping at most one section's picked manufacturing
  // line. A source switch re-solves every section against a DIFFERENT set of machines (the shared example account
  // vs the owner's own), so a manufacturing line and solution picked against the old source mean nothing against
  // the new one. The remount re-seeds each section from the address bar and the saved quote, so leaving the old
  // selection there restores a plan the new source does not list and a solution priced against it -- which is what
  // confused the section after a switch. So drop the picked line and chosen solution from the address bar and from
  // this page's own mirrors, and clear the saved solutions the sections re-seed from on remount. selected_lists is
  // nulled, not set to [], to match the "no selection" the server serializes on a fresh load -- so this optimistic
  // state and the reloaded one read identically. The general section is left alone: it holds the quote-wide
  // pricing sub-sections, not a manufacturing-line solution (as the server reset does too).
  //   keepLineForIndex / keepLineIdentifier keep ONE section's picked line through the switch -- used by the
  //   standard-lines fallback, which flips to Standard AND carries the line the owner borrowed so the section
  //   opens on its solutions rather than an empty slate (see useStandardLineForSection). A picked line lives in
  //   the address bar (not selected_lists), so seeding it here and nulling selected_lists agree: no solution is
  //   chosen yet, only a line.
  const resetSelectionsForSourceSwitch = ({ keepLineForIndex = null, keepLineIdentifier = null } = {}) => {
    priceableSections.forEach(({ index }) => {
      clearSelectedSolution(index);
      if (index === keepLineForIndex) {
        writeSelectedManufacturingLineList(index, keepLineIdentifier);
      } else {
        clearSelectedManufacturingLineList(index);
      }
    });
    setPickedLineBySection(keepLineForIndex === null ? {} : { [keepLineForIndex]: keepLineIdentifier });
    setChosenSolutionBySection({});
    window.user_quote_nav_sections = (window.user_quote_nav_sections || []).map((section) => (
      section.part_type === 'general' ? section : { ...section, selected_lists: null }
    ));
  };

  // Update the global FIRST (every Frank URL builder reads it at fetch time), then the state -- which remounts the
  // sections via the key below, so they re-solve against the new source -- then persist the new default. The
  // listing is served live from the request param the fetches carry, so a failed persist only loses the stored
  // seed: the page already shows the new source and there is nothing to roll back. The matching server-side reset
  // rides this persist (frank#manufacturing_lines_source clears selected_lists when the source changes), so a
  // later reload stays on the clean slate too.
  const persistManufacturingLinesSource = (next) => {
    setManufacturingLinesSource(next);
    setSourceState(next);
    PostRequest({
      url: `/mcc/${window.user_quote_id}/manufacturing-lines-source`,
      data: { manufacturing_lines_source: next },
      // PostRequest calls `callback` unconditionally on a 2xx; without one a successful persist would throw. The
      // stored value is only the seed (the live answer rides the request param), so there is nothing to do with
      // the response.
      callback: () => {},
    }).catch(() => {});
  };

  // The quote-wide toggle: switch the source and clear the slate. (An owner switching to their own manufacturing
  // lines with no printing machines yet lands on an empty listing whose add-a-machine picker shows INLINE -- see
  // ManufacturingLineLists' empty state -- so there is no popup to open here.)
  const changeManufacturingLinesSource = (next) => {
    if (next === manufacturingLinesSource) { return; }

    resetSelectionsForSourceSwitch();
    persistManufacturingLinesSource(next);
  };

  // BORROW A STANDARD LINE when the owner's OWN lines came up empty. The standard-lines fallback (shown under the
  // "no manufacturing lines match" finding, on the owner's own lines only) reports the line the owner picked; flip
  // the whole quote to Standard, keep THAT section on THAT line through the remount, and open its Solution stage --
  // so the borrowed line is priced against the Standard account it came from, and the quote's Standard toggle now
  // reads selected. The other sections are cleared like any source switch.
  const useStandardLineForSection = (sectionIndex, identifier) => {
    resetSelectionsForSourceSwitch({ keepLineForIndex: sectionIndex, keepLineIdentifier: identifier });
    persistManufacturingLinesSource(STANDARD);
    openStage(sectionIndex, 'solution');
  };

  // When the catalogue the sections are priced against changes, re-solve them against the new catalogue. This
  // fires from two kinds of trigger. IN THIS PAGE: the owner finishing an edit in the filter island's
  // ManageOwnMachinesButton or the empty-state EmptyStateMachineAdder, each firing ONCE when its machine picker
  // CLOSES having made a change -- not on every save: a per-save re-solve remounts the very subtree that holds
  // the open picker and tears it down mid-edit, so the owner could not add several in a row. IN ANOTHER TAB:
  // this tab becoming visible/focused again after the owner edited their unit prices on the settings page.
  // Recompute the response-cache fingerprint (the browser reuses a stored answer only while it matches, and a
  // catalogue change moved it), point the page's globals at the fresh one, DROP the now-stale cached answers, then
  // tell the sections to re-fetch (a genuine miss, now the cache is cleared) -- and the server recomputes because
  // the same change moved its answer's digest. There are TWO ways to tell them, for two triggers:
  //   machinesRevision -- part of the section KEY, so bumping it REMOUNTS the section. An IN-PAGE edit (adding /
  //     pricing a machine here) wants that: a just-completed setup step re-seeds every bit of the section's state
  //     from the address bar cleanly.
  //   dataRevision -- a PROP the section's fetches depend on, NOT part of its key, so bumping it re-runs those
  //     fetches IN PLACE. A TAB RETURN that finds a real change (the owner edited prices in another tab) uses this:
  //     the section keeps its identity -- no blank, no scroll jump, the pricing tree stays put -- and just its
  //     data refreshes, with the normal brief in-place loading. Remounting an open page on tab return is the
  //     distracting blink this avoids.
  const [machinesRevision, setMachinesRevision] = useState(0);
  const [dataRevision, setDataRevision] = useState(0);
  // One refresh at a time: a single tab switch can fire visibilitychange AND focus, and an in-page edit can race
  // a focus. The in-flight request is already fetching the freshest fingerprint, so a second would only duplicate
  // the work.
  const fingerprintRefreshInFlight = useRef(false);
  // An inline empty-state add form (add machine / add material) is open in a section -- counted, since the
  // stacked page view can have more than one open at once. A SPECULATIVE visibility/focus refresh must not
  // remount the sections while one is (the remount tears the open form down mid-edit -- the owner tabs away and
  // back and lands on the re-solved listing). Such a refresh defers its remount here and it runs when the last
  // form closes. See the effect below and the frank:own-setup-open / -close events the adders fire.
  const ownSetupFormOpenCountRef = useRef(0);
  const pendingInPlaceRefreshRef = useRef(false);
  useEffect(() => {
    // The catalogue moved, so this quote's cached answers are stale. The response cache is keyed by request
    // URL -- which a price change does not move -- and nothing else evicts it, so the re-listed sections would
    // just re-serve the pre-change answers unless it is dropped here FIRST. clearResponseCache also drops this
    // quote's persisted (IndexedDB) copies; re-pointing persistence at the fresh fingerprint (hydrateResponseCache)
    // is what then stores the answers the remount fetches under the NEW stamp, so they are read back on the next
    // load rather than pruned as stale. Shared by every trigger below AND by the deferred replay when the last
    // add form closes, so "the catalogue moved" is handled the one way.
    const dropStaleAnswers = () => {
      clearResponseCache();
      if (window.frank_response_cache_fingerprint) {
        hydrateResponseCache({
          fingerprint: window.frank_response_cache_fingerprint,
          variant: window.frank_response_cache_variant,
          userId: window.frank_response_cache_user_id,
          quoteId: user_quote_id
        });
      }
    };
    // An in-page edit re-solves by REMOUNTING the section (see machinesRevision) -- the just-completed setup step
    // re-seeds the section's state from the address bar.
    const remountSections = () => { dropStaleAnswers(); setMachinesRevision((revision) => revision + 1); };
    // A tab return that found a real change re-fetches IN PLACE (see dataRevision) -- the section keeps its
    // identity and only its data refreshes, so there is no blank/scroll-jump blink on coming back to the tab.
    const refreshSectionsInPlace = () => { dropStaleAnswers(); setDataRevision((revision) => revision + 1); };

    // remountAlways distinguishes the two triggers. An in-page machine edit KNOWS the catalogue moved and always
    // wants the re-solve (and keeps the original behaviour of re-solving even if the fingerprint fetch fails). A
    // visibility/focus refresh runs speculatively -- most tab switches change nothing -- so it only refreshes in
    // place when the CATALOGUE actually moved in another tab (catalogue_stamp, not an in-page nav-section edit and
    // not the code deploy), leaving an unaffected tab untouched (one cheap GET, no re-fetch).
    const refreshResponseFingerprint = ({ remountAlways }) => {
      if (fingerprintRefreshInFlight.current) { return; }
      fingerprintRefreshInFlight.current = true;

      // no-store + XMLHttpRequest: force a fresh recompute (a cached fingerprint would be the stale one), and
      // identify as XHR like the rest of the app's JSON gets.
      fetch(`/mcc/${window.user_quote_id}/response-cache-fingerprint`,
        { cache: 'no-store', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          if (!data || !data.fingerprint) { return false; }
          // Refresh only on a CROSS-TAB CATALOGUE change -- the owner editing a machine price or adding/removing a
          // machine on the settings page in ANOTHER tab. catalogue_stamp is the subset of the fingerprint that can
          // move there. The REST of the quote's inputs -- a section's lamination / printing / wastage, the dieline
          // options -- are edited IN this page, where the change already re-prices in place; but they move
          // inputs_stamp too, so comparing inputs_stamp made every window focus after such an in-page edit (a
          // native <select> refocuses the window) look like an external change and tear the section down to re-list
          // and re-score it. A code deploy moves neither stamp, so it does not blink an open page either. Fall back
          // to inputs_stamp, then the whole fingerprint, on a server too old to send catalogue_stamp.
          const dataChanged = data.catalogue_stamp !== undefined
            ? data.catalogue_stamp !== window.frank_response_cache_catalogue_stamp
            : (data.inputs_stamp !== undefined
              ? data.inputs_stamp !== window.frank_response_cache_inputs_stamp
              : data.fingerprint !== window.frank_response_cache_fingerprint);
          // Keep the whole cache key in sync, not just the validity stamp: a machine edit does not move the
          // variant/user_id today, but reading them off the same response means the page cannot drift into
          // fetching under a new fingerprint while hydrating/persisting under a stale variant. Harmless when
          // nothing is remounted below: the cache reads the fingerprint captured at hydrate, not this global.
          window.frank_response_cache_fingerprint = data.fingerprint;
          window.frank_response_cache_inputs_stamp = data.inputs_stamp;
          window.frank_response_cache_catalogue_stamp = data.catalogue_stamp;
          window.frank_response_cache_variant = data.variant;
          window.frank_response_cache_user_id = data.user_id;
          return dataChanged;
        })
        .catch(() => false)
        .then((dataChanged) => {
          if (!remountAlways && !dataChanged) { return; }

          // Hold a SPECULATIVE in-place refresh while an inline add form is open: even an in-place data refresh
          // re-runs the section's fetches, which tears the open machine/price form down mid-edit (the owner tabs
          // away and back and lands on the re-solved listing). It is deferred and applied when the last form closes
          // (onOwnSetupClose). A genuine own-machines-changed (remountAlways -- a completed add) is exempt: that IS
          // the moment to re-solve.
          if (!remountAlways && ownSetupFormOpenCountRef.current > 0) {
            pendingInPlaceRefreshRef.current = true;
            return;
          }
          // An in-page edit remounts (re-seeds from the URL); a tab return refreshes in place (no blink).
          if (remountAlways) { remountSections(); } else { refreshSectionsInPlace(); }
        })
        .finally(() => { fingerprintRefreshInFlight.current = false; });
    };

    const onOwnMachinesChanged = () => {
      // A full re-solve is happening now, so any refresh deferred behind an open form is subsumed by it.
      pendingInPlaceRefreshRef.current = false;
      refreshResponseFingerprint({ remountAlways: true });
    };
    // The catalogue can change in ANOTHER tab: the owner manages their unit prices on the settings page while
    // this /mcc tab stays open. Nothing re-checks the fingerprint while a tab is open, so re-check it when the
    // tab is looked at again -- visibilitychange for a tab switch, focus for a side-by-side layout where this
    // window regains focus without ever being hidden.
    const onBecameVisible = () => {
      if (document.visibilityState !== 'visible') { return; }
      refreshResponseFingerprint({ remountAlways: false });
    };
    const onFocus = () => refreshResponseFingerprint({ remountAlways: false });

    // The inline empty-state adders announce when one opens/closes, so a speculative refresh holds its in-place
    // re-fetch rather than tearing the open form down. Counted, since the stacked page view can have several open
    // at once; when the last closes, any deferred refresh runs. It is always the tab-return path (remountAlways is
    // never deferred), so it replays in place.
    const onOwnSetupOpen = () => { ownSetupFormOpenCountRef.current += 1; };
    const onOwnSetupClose = () => {
      ownSetupFormOpenCountRef.current = Math.max(0, ownSetupFormOpenCountRef.current - 1);
      if (ownSetupFormOpenCountRef.current === 0 && pendingInPlaceRefreshRef.current) {
        pendingInPlaceRefreshRef.current = false;
        refreshSectionsInPlace();
      }
    };

    window.addEventListener('frank:own-machines-changed', onOwnMachinesChanged);
    window.addEventListener('frank:own-setup-open', onOwnSetupOpen);
    window.addEventListener('frank:own-setup-close', onOwnSetupClose);
    document.addEventListener('visibilitychange', onBecameVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('frank:own-machines-changed', onOwnMachinesChanged);
      window.removeEventListener('frank:own-setup-open', onOwnSetupOpen);
      window.removeEventListener('frank:own-setup-close', onOwnSetupClose);
      document.removeEventListener('visibilitychange', onBecameVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Open on the section a refresh / shared link picked a plan in (its manufacturing_line_<index> is in the
  // URL), so the restored selection reopens on its own section. Otherwise the first part section (e.g. Carton)
  // -- the manufacturing costs it lists are what the page is FOR, so it is what a user wants to see first. A
  // quote with no part section (only the nav builder's 'general' catch-all) has no section tab, and falls back
  // to Manage.
  const restoredSection = priceableSections.find(
    ({ index }) => readSelectedManufacturingLineList(index) !== null
  );
  const initialSectionIndex = restoredSection ? restoredSection.index : priceableSections[0]?.index;
  const [activeTab, setActiveTab] = useState(
    initialSectionIndex != null ? `section_${initialSectionIndex}_line` : 'manage'
  );

  // activeTab stays the single source of truth, a flat `section_<index>_<stage>` key (so the URL-restore,
  // initial-tab and wizard logic all keep working unchanged); the two-level bar is DERIVED from it. A
  // section's top tab is "active" when activeTab is any of that section's three stages.
  const activeSectionMatch = activeTab.match(/^section_(\d+)_(line|solution|pricing)$/);
  const activeSectionIndex = activeSectionMatch ? Number(activeSectionMatch[1]) : null;
  const activeStage = activeSectionMatch ? activeSectionMatch[2] : null;

  // THE SECTION'S STAGES ARE A SEQUENCE, NOT THREE VIEWS. A solution is a way of making the section with ONE
  // manufacturing line, and a price is what THAT solution costs -- so neither means anything until the stage
  // before it has been answered, and the tabs say so by being disabled rather than by letting the user in and
  // showing them an empty panel.
  //
  // What each stage is answered BY:
  //   line     -- always open; it is where the sequence starts.
  //   solution -- a manufacturing line is picked for this section (mirrored in the address bar, so a refresh
  //               and a shared link restore it).
  //   pricing  -- a solution is chosen. Chosen, not yet priced: the Pricing tab is what COMMITS the marked
  //               card (see ManufacturingLineLists' pricing commit), so gating on "already priced" would lock
  //               the door from the inside and the user could never reach it.
  //
  // A LINKED section is left alone. It picks nothing and solves nothing -- both come from the section that
  // leads it -- so its tabs explain that instead, and its Pricing follows the solution it inherited.
  const savedSolutionCountFor = (index) => {
    const section = (window.user_quote_nav_sections || []).find((each) => each.sort_order === index);
    return (section && section.selected_lists ? section.selected_lists : []).length;
  };
  const [pickedLineBySection, setPickedLineBySection] = useState(() => priceableSections.reduce(
    (picked, { index }) => ({ ...picked, [index]: readSelectedManufacturingLineList(index) }), {}
  ));
  const [chosenSolutionBySection, setChosenSolutionBySection] = useState(() => priceableSections.reduce(
    (chosen, { index }) => ({ ...chosen, [index]: savedSolutionCountFor(index) > 0 }), {}
  ));

  // The section's own selection, or -- for a linked section -- the leading section's, which is where its
  // manufacturing line and solution actually come from.
  const stageSourceIndex = (index) => {
    const parent = linkedParentOf(window.user_quote_nav_sections, index);
    return parent ? parent.sort_order : index;
  };
  const stageIsOpen = (index, stage) => {
    if (stage === 'line') { return true; }
    const source = stageSourceIndex(index);
    if (stage === 'solution') {
      return Boolean(pickedLineBySection[source]) || chosenSolutionBySection[source] === true;
    }
    return chosenSolutionBySection[index] === true || chosenSolutionBySection[source] === true;
  };

  // The server is the authority on what is selected, and it says so through these two events: select-solution
  // publishes navSectionsUpdated, and a reset (including the one a changed manufacturing line triggers)
  // publishes navSectionsReset. Both refresh window.user_quote_nav_sections first, so re-reading it is what
  // keeps a tab from staying open on a solution that no longer exists.
  const rereadSavedSolutions = () => setChosenSolutionBySection(
    () => priceableSections.reduce((chosen, { index }) => ({ ...chosen, [index]: savedSolutionCountFor(index) > 0 }), {})
  );
  useEffect(() => {
    window.addEventListener('navSectionsUpdated', rereadSavedSolutions);
    window.addEventListener('navSectionsReset', rereadSavedSolutions);
    return () => {
      window.removeEventListener('navSectionsUpdated', rereadSavedSolutions);
      window.removeEventListener('navSectionsReset', rereadSavedSolutions);
    };
  }, []);

  // A MARK DOES NOT OUTLIVE THE VISIT IT WAS MADE IN. Choosing a solution card only MARKS it; the Pricing tab
  // is what commits it. Leaving the section unmounts its tree and the uncommitted mark goes with it -- so this
  // page's memory of it has to go too, or Pricing stays open on the strength of a choice that is no longer on
  // screen. Falling back to what the quote has SAVED is what leaves an already-priced section open.
  //
  // Keyed on the active SECTION, not the stage: the three stages share one mounted section (see the key on
  // LayoutNavSectionList), so walking Solution -> Pricing keeps the mark, which is the whole point of it.
  useEffect(() => { rereadSavedSolutions(); }, [activeSectionIndex]);

  // A STAGE CAN CLOSE UNDER THE USER'S FEET. Ticking a filter re-lists the section and drops the picked plan
  // with it, so someone standing on the Solution stage is suddenly standing on a stage with nothing to solve.
  // Walk them back to the Manufacturing Line stage rather than leave them on a closed tab being asked to go
  // and pick one -- the tab is disabled now, so that prompt is a dead end.
  useEffect(() => {
    if (activeSectionIndex === null || activeStage === null) { return; }
    if (stageIsOpen(activeSectionIndex, activeStage)) { return; }

    openStage(activeSectionIndex, 'line');
  }, [activeSectionIndex, activeStage, pickedLineBySection, chosenSolutionBySection]);

  // Remember which stage each section was last left on, so returning to a section reopens where the user was
  // rather than jumping back to Manufacturing Line. Defaults to the first stage ('line') for an unvisited one.
  const lastStageBySection = useRef({});
  // Reopening a section on a stage that has since been closed (its solution was invalidated while the user was
  // elsewhere) would land them on a disabled tab, so the remembered stage is only honoured while it is open.
  const openSection = (index) => {
    const remembered = lastStageBySection.current[index] || 'line';
    setActiveTab(`section_${index}_${stageIsOpen(index, remembered) ? remembered : 'line'}`);
  };
  const openStage = (index, stage) => {
    lastStageBySection.current[index] = stage;
    setActiveTab(`section_${index}_${stage}`);
  };

  // The stage sub-menu hangs UNDER its parent section tab, like a drop-down sub-menu, rather than floating
  // centred. Since the active section tab's horizontal position depends on the labels before it, we measure
  // it: activeTabRef is put on the active section's top tab, tabRowRef on the row both levels start from, and
  // the sub-menu is indented by the gap between them. Re-measured whenever the active tab or the width changes.
  const tabRowRef = useRef(null);
  const activeTabRef = useRef(null);
  const [subMenuOffset, setSubMenuOffset] = useState(0);

  // The guided setup wizard auto-opens on a quote that has not been set up yet -- nothing priced -- so a fresh
  // quote is walked through quantity -> pick a line -> pick a solution per section. A quote that already has a
  // priced section opens straight to the page; the "Guided setup" button reopens the wizard any time. While it
  // is open the active tab's section body is not rendered, so the wizard's own ManufacturingLineLists is the
  // only one on screen (two would fight over the manufacturing_line_<index> URL state); closing it re-renders
  // the section, which reads the now-priced state the wizard persisted.
  //
  // "Not set up yet" is not the same as "nothing selected right now". Changing a manufacturing line discards
  // the solution that was chosen on the old one, which can leave a quote with nothing priced -- and a user who
  // has just made a choice and refreshed should land on their listing, not be walked through a wizard they
  // finished days ago. A picked manufacturing line in the address bar is what tells the two apart: it means
  // this quote is mid-flow, in the hands of someone who is choosing for themselves.
  const anySectionPriced = (window.user_quote_nav_sections || [])
    .some((section) => section.selected_lists && section.selected_lists.length > 0);
  const anySectionMidFlow = priceableSections
    .some(({ index }) => readSelectedManufacturingLineList(index) !== null);
  const [wizardOpen, setWizardOpen] = useState(
    sectionTabs.length > 0 && !anySectionPriced && !anySectionMidFlow
  );

  // Closing the wizard FOLLOWS THE USER TO THE PRICE. The wizard reports the section it priced -- the one the
  // user just picked a manufacturing line and a solution for -- so the page opens that section's Pricing tab
  // instead of dropping the user back on the Manufacturing Line tab it happened to be sitting on, which would
  // ask them to make a choice they have already made. A wizard dismissed before it priced anything reports no
  // section, and leaves the active tab alone.
  const closeWizard = ({ pricedSectionIndex } = {}) => {
    setWizardOpen(false);
    if (pricedSectionIndex != null) { openStage(pricedSectionIndex, 'pricing'); }
  };

  useEffect(() => { window.user_quote_id = user_quote_id; }, []);

  // NOTHING RENDERS UNTIL THE PERSISTED ANSWERS ARE BACK IN THE CACHE. The sections below start fetching the
  // moment they mount, and the cache they consult is read synchronously -- so a section mounted before
  // hydration would miss every entry and re-list, re-solve and re-score a quote the browser already has the
  // answers for. Gating here, at the one component the whole /mcc page hangs off, is what lets every reader
  // downstream stay exactly as it was.
  //
  // The wait is milliseconds against the minutes of scoring it saves, and it is bounded twice over: whichever
  // of hydration or the timeout comes first releases the page, and a page with no fingerprint has nothing to
  // wait FOR (it persists nothing), so it renders at once exactly as it always did.
  const [responseCacheHydrated, setResponseCacheHydrated] = useState(
    () => !window.frank_response_cache_fingerprint
  );
  useEffect(() => {
    let released = false;
    const release = () => { if (!released) { released = true; setResponseCacheHydrated(true); } };

    hydrateResponseCache({
      // Absent on any page that does not render one, which turns persistence off and leaves the in-memory
      // cache behaving exactly as it did before.
      fingerprint: window.frank_response_cache_fingerprint,
      // WHICH viewing of those answers this is (language + unit). Held apart from the fingerprint so the
      // viewings coexist instead of evicting one another -- see Frank::ResponseCacheFingerprint#variant.
      variant: window.frank_response_cache_variant,
      // WHOSE they are. The store lives in the browser profile, so a load by anyone else deletes what is not
      // theirs rather than leaving one customer's machines and prices in another's browser.
      userId: window.frank_response_cache_user_id,
      quoteId: user_quote_id
    }).then(release).catch(release);

    const timer = setTimeout(release, CACHE_HYDRATION_TIMEOUT_MILLISECONDS);
    return () => { clearTimeout(timer); };
  }, []);

  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (msg) => { setCount(() => msg.detail); } }
  ]);

  const readOnly = window.user_quote_finalised;

  const isSectionTab = activeSectionIndex !== null;

  // How wide the active tab's body is drawn. A FORM tab is held to a readable, centred ~620px column so its
  // labels sit near their inputs; a SECTION tab (and the dieline) gets the full container width for its cards.
  // The GENERAL form tab is the exception: it renders through QuoteBuilderNavSection, which insets its form
  // into a Bootstrap col-lg-8 (8 of 12 columns), so its column is widened by 12/8 -- 620 -> 930 -- to land
  // that inset form at the SAME ~620px the other form tabs (Manage / Customer) fill directly, keeping every
  // form tab one width.
  const tabBodyStyle = (() => {
    if (isSectionTab || activeTab === 'dieline') { return undefined; }
    if (activeTab === 'general') { return { maxWidth: '930px', margin: '0 auto' }; }
    return { maxWidth: '620px', margin: '0 auto' };
  })();

  // The sentence for the marker on a LINKED section's Manufacturing Line tab, or null when this section chooses
  // for itself. Naming the section that leads is the useful half -- "somewhere else" would leave the reader
  // hunting -- so the unnamed wording is only for a stale payload whose parent is no longer in the list.
  const linkedParentSection = isSectionTab
    ? linkedParentOf(window.user_quote_nav_sections, activeSectionIndex)
    : null;
  const linkedSectionNote = (() => {
    if (!isSectionTab) { return null; }
    if (linkedParentSection) {
      return t('quote_builder.frank_linked_section_body', { section: linkedParentSection.label });
    }
    const section = (window.user_quote_nav_sections || [])
      .find((candidate) => candidate.sort_order === activeSectionIndex);
    return section && section.solution_user_quote_nav_section_id
      ? t('quote_builder.frank_linked_section_body_unnamed')
      : null;
  })();

  useLayoutEffect(() => {
    if (!isSectionTab || !activeTabRef.current || !tabRowRef.current) {
      setSubMenuOffset(0);
      return undefined;
    }
    const measure = () => {
      const tabLeft = activeTabRef.current.getBoundingClientRect().left;
      const rowLeft = tabRowRef.current.getBoundingClientRect().left;
      setSubMenuOffset(Math.max(0, tabLeft - rowLeft));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); };
    // responseCacheHydrated belongs here with the tab: until it is true this component renders null, so the
    // tabs this measures are not in the document yet and there is nothing to measure against. Without it the
    // measurement only ever ran against that empty first render, and the stage sub-menu stayed pinned to the
    // far left of the row instead of hanging under its own section tab.
  }, [activeTab, isSectionTab, responseCacheHydrated]);

  const renderActiveTab = () => {
    if (activeTab === 'manage') {
      return (
        <>
          <QuoteBuilderForm config={config.general} readOnly={readOnly} />
          <QuoteBuilderManagePanel readOnly={readOnly} />
        </>
      );
    }
    if (activeTab === 'customer') {
      return <QuoteBuilderForm config={config.customer} readOnly={readOnly} />;
    }
    if (activeTab === 'general' && generalSection) {
      // The general section's quote-wide pricing sub-sections (Profit Margin, Transport, ...). Rendered
      // straight through QuoteBuilderNavSection -- whose inner QuoteBuilder fetches those sub-sections from the
      // server -- rather than the legacy GeneralNavSection, which only renders once it receives a
      // nav_section_general_part_type event fired by SelectedList (a component Frank never mounts, so the event
      // never comes). An empty payload is safe: a general section has no parts or members, so
      // QuoteBuilderNavSection takes navSection.drawing as its item and never reads payload.items. noTitle
      // because the "General" tab already names the section, so the inner heading would just repeat it.
      return (
        <QuoteBuilderNavSection
          index={generalSection.index}
          config={config}
          payload={{}}
          presentedOptions={{}}
          navSection={generalSection}
          noTitle
        />
      );
    }
    if (activeTab === 'dieline') {
      // Show the dieline SVG (rendered server-side with its dimension texts) above the material +
      // dimension table, so the user can see the dieline being priced. dieline_svg is inline SVG markup.
      return (
        <>
          {dieline_svg && (
            <div
              style={{ maxWidth: '930px', margin: '0 auto 15px' }}
              dangerouslySetInnerHTML={{ __html: dieline_svg }}
            />
          )}
          <div style={{ maxWidth: '620px', margin: '0 auto' }}>
            <PresentedDielineOptions />
          </div>
        </>
      );
    }

    const sectionTab = sectionTabs.find((tab) => tab.key === activeTab);
    if (sectionTab) {
      // The stage AFTER the one on screen, in the section's fixed order (Manufacturing Line -> Solution ->
      // Pricing). The card's "Next" button walks the user there; the Pricing tab has no next, so it is null and
      // no Next button is offered on the priced view.
      const stageOrder = sectionStageTabs.map((stageTab) => stageTab.stage);
      const nextStage = stageOrder[stageOrder.indexOf(sectionTab.stage) + 1] || null;
      // A single-section list, so the section's own Frank-mode material-sheet fetch + gating are reused. The
      // KEY is the section (not the stage tab), so the three stage tabs share ONE ManufacturingLineLists
      // instance -- the picked plan and its solved solutions carry across tabs; only stageView changes.
      // Picking a manufacturing line or a solution stays on its own tab (so the selected one, and the dimmed
      // rest, stay visible); the user moves on with the tabs. Visiting the Pricing tab commits + prices the
      // marked solution.
      return (
        <LayoutNavSectionList
          // The source is in the key so switching it REMOUNTS the section, which re-seeds its state and re-fetches
          // against the new source (the URL builders read window.frank_manufacturing_lines_source).
          key={`section_${sectionTab.sectionIndex}_${manufacturingLinesSource}_${machinesRevision}`}
          // Bumped by a tab return that found a real change -- the section's data fetches depend on it, so they
          // re-run IN PLACE (no remount, no blink). NOT in the key above, unlike machinesRevision.
          dataRevision={dataRevision}
          navSections={[navSections[sectionTab.sectionIndex]]}
          count={count}
          config={config}
          stageView={sectionTab.stage}
          // What a USER click answered, so the stage after it opens straight away rather than on the next
          // render that happens to re-read the address bar. Restores do not fire these -- the seeds above
          // cover those.
          onLinePicked={(identifier) => setPickedLineBySection((picked) => (
            picked[sectionTab.sectionIndex] === identifier
              ? picked
              : { ...picked, [sectionTab.sectionIndex]: identifier }
          ))}
          onSolutionPicked={(group) => setChosenSolutionBySection(
            (chosen) => ({ ...chosen, [sectionTab.sectionIndex]: Boolean(group) })
          )}
          onGoToNextStage={nextStage ? () => openStage(sectionTab.sectionIndex, nextStage) : null}
          onGoToManufacturingLine={() => openStage(sectionTab.sectionIndex, 'line')}
          // The owner's own lines came up empty and they borrowed a standard one: flip the quote to Standard,
          // keep this section on that line, and open its solutions (see useStandardLineForSection).
          onUseStandardLine={(identifier) => useStandardLineForSection(sectionTab.sectionIndex, identifier)}
        />
      );
    }
    return null;
  };

  // After every hook, so the rules of hooks are kept: the page holds still for the moment it takes to read its
  // cached answers back (see the hydration effect), rather than mounting sections that would re-ask for them.
  if (!responseCacheHydrated) { return null; }

  return (
    <ErrorBoundary componentName="FrankQuoteBuilderPage" errorContext={{ user_quote_id: user_quote_id }}>
      <CacheProvider initialCache={window.user_quote_section_properties_cached}>
        <LayoutContext.Provider value={LayoutContextValues}>
          <DebugPopupProvider>
          <div className={`${styles.component_container} container`}>
            {/* TOP LEVEL: one tab per section plus Manage / Customer / Dieline, on the shared divider, with the
                "Guided setup" button on the right. Aligning to flex-end sits the tab underlines on the border.
                Using the shared .dct-tabs / .dct-tab design-language classes (brand underline on the active
                tab) rather than a per-page inline style. */}
            <div ref={tabRowRef} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '2px solid var(--rule)' }}>
              {/* The page breadcrumb (parent model group | group | catalog) shares the tab row's LEFT so it costs
                  no extra vertical space; its navigation stays even though the source control now sits where it
                  used to (the stage-tabs row). Sits on the divider like the tabs (paddingBottom matches the
                  Guided-setup button). */}
              {header && (
                <div className="page-in-name" style={{ fontSize: '15px', margin: 0, paddingBottom: '8px', whiteSpace: 'nowrap' }}>
                  <span>
                    {header.demo
                      ? header.demoLabel
                      : <a href={header.parentUrl}>{header.parentName}</a>}
                  </span>
                  {/* The shared `.page-in-name h1` rule forces 23px; override it inline so this breadcrumb sits at
                      the tab-label size. */}
                  <h1 style={{ fontSize: 'inherit' }}>
                    {' | '}<a href={header.groupUrl}>{header.groupName}</a>
                    {' | '}<a href={header.catalogUrl}>{header.catalogName}</a>
                  </h1>
                </div>
              )}
              <div className="dct-tabs" style={{ flex: 1, border: 'none', justifyContent: 'center', flexWrap: 'wrap' }}>
                {topTabs.map((tab) => {
                  const isActive = tab.sectionIndex != null
                    ? activeSectionIndex === tab.sectionIndex
                    : activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      ref={isActive && tab.sectionIndex != null ? activeTabRef : null}
                      className={`dct-tab${isActive ? ' is-active' : ''}`}
                      style={{ marginBottom: '-2px' }}
                      onClick={() => (tab.sectionIndex != null ? openSection(tab.sectionIndex) : setActiveTab(tab.key))}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
              {/* Reopen the guided setup wizard any time; only shown when there is a part section to set up. */}
              {sectionTabs.length > 0 && (
                <div style={{ paddingBottom: '8px', paddingLeft: '12px' }}>
                  <button
                    type="button"
                    className="dct-button dct-button--secondary dct-button--small"
                    onClick={() => setWizardOpen(true)}
                  >
                    {t('setup_wizard.open')}
                  </button>
                </div>
              )}
            </div>

            {/* SECOND LEVEL: the active section's three stages, in order, shown ONLY when a section tab is
                selected. They are a subordinate segmented control (not a second row of the same underline tab),
                numbered because the user walks them in sequence: pick a Manufacturing Line, then a Solution,
                then see the Pricing. Left-indented by subMenuOffset so the control hangs UNDER its parent
                section tab, like a drop-down sub-menu. The page breadcrumb (parent | model | catalog name) is
                pushed to the right of this same line, so the two share one row and the section content starts
                right below. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', margin: '10px 0 0', minHeight: '34px' }}>
              {isSectionTab && (
                <div className={frankStyles.stageTabs} role="tablist" aria-label={t('quote_builder.frank_section_stages')} style={{ marginLeft: subMenuOffset }}>
                  {sectionStageTabs.map((stageTab, stepIndex) => {
                    const isActive = activeStage === stageTab.stage;
                    // Closed until the stage before it has been answered -- see stageIsOpen. The title says
                    // WHY, so a disabled tab is an instruction rather than a dead end.
                    const isOpen = stageIsOpen(activeSectionIndex, stageTab.stage);
                    return (
                      <button
                        key={stageTab.stage}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        disabled={!isOpen}
                        title={isOpen ? undefined : t(`quote_builder.frank_stage_locked_${stageTab.stage}`)}
                        className={`${frankStyles.stageTab}${isActive ? ` ${frankStyles.stageTabActive}` : ''}`
                          + `${isOpen ? '' : ` ${frankStyles.stageTabLocked}`}`}
                        onClick={() => openStage(activeSectionIndex, stageTab.stage)}
                      >
                        <span className={frankStyles.stageNumber}>{stepIndex + 1}</span>
                        {t(stageTab.labelKey)}
                        {/* A LINKED section's manufacturing line comes from the section that leads it, so there
                            is nothing to choose on this stage. Said as a marker on the tab rather than a panel
                            above the answer: it is read once, and after that a block that size is only ever in
                            the way of what the reader came for. */}
                        {stageTab.stage === 'line' && linkedSectionNote ? (
                          <span className={frankStyles.stageTabInfo} data-frank-linked-tab-info>
                            {/* The glyph is decoration; the sentence below is the content, so it is the part a
                                screen reader should read. */}
                            <span aria-hidden="true">i</span>
                            <span className={frankStyles.stageTabTooltip} role="tooltip">
                              {linkedSectionNote}
                            </span>
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* The quote-wide manufacturing-lines source, on the RIGHT of this row (marginLeft:auto), where the
                  breadcrumb used to sit. The stage tabs stay the FIRST child so "Üretim Parkuru" keeps its
                  subMenuOffset alignment directly under the active section tab (e.g. Karton). */}
              <div style={{ marginLeft: 'auto' }}>
                <StandardManufacturingLinesToggle
                  value={manufacturingLinesSource}
                  onChange={changeManufacturingLinesSource}
                />
              </div>
            </div>

            {/* A FORM tab is constrained to a readable column, centered, so its labels sit near their inputs
                rather than stretching label-left / input-right across the whole page. A SECTION tab is not:
                it lists the manufacturing line cards, which are sized as a fraction of this container (4 to a
                row, like the legacy listing) -- in a 620px column each card is ~139px and its machine columns
                are crushed to ~60px. So the section tabs get the container's full width. */}
            {/* The active tab's body is hidden while the wizard is open, so only the wizard's own
                ManufacturingLineLists is mounted (two would fight over the manufacturing_line_<index> URL
                state). Closing the wizard re-renders it, now reading the priced state the wizard persisted. */}
            {!wizardOpen && (
              <div style={tabBodyStyle}>
                {renderActiveTab()}
              </div>
            )}
          </div>

          {wizardOpen && (
            <FrankSetupWizard
              navSections={navSections}
              config={config}
              onClose={closeWizard}
              manufacturingLinesSource={manufacturingLinesSource}
              onManufacturingLinesSourceChange={changeManufacturingLinesSource}
              // Borrowing a standard line from the empty-state fallback flips the quote to Standard, keeps that
              // section on the borrowed line and opens its solutions -- the same handler the page tabs use.
              onUseStandardLineForSection={useStandardLineForSection}
              machinesRevision={machinesRevision}
              dataRevision={dataRevision}
            />
          )}
          </DebugPopupProvider>
        </LayoutContext.Provider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default FrankQuoteBuilderPage;
